import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import wingitDesign from '../designSystem';

export default function TechLogReviewWorkspace({
  aircraft,
  bookings = [],
  userMap = {},
  techLogMap = {},
  selectedBookingId = '',
  saveNoticeMap = {},
  onSaveDraft,
  onAcceptEntry,
  onExport,
  annualCheckDate,
  annualCountdownDays,
  onAnnualCheckDateChange,
  liveLog,
}) {
  const ds = wingitDesign;

  const [activeBookingId, setActiveBookingId] =
    useState(() =>
      String(
        selectedBookingId ||
          bookings[0]?.id ||
          ''
      )
    );

  const [draftMap, setDraftMap] = useState({});

  useEffect(() => {
    if (bookings.length === 0) {
      setActiveBookingId('');
      return;
    }

    const activeStillExists = bookings.some(
      (booking) =>
        String(booking.id) ===
        String(activeBookingId)
    );

    if (!activeStillExists) {
      setActiveBookingId(
        String(
          selectedBookingId ||
            bookings[0]?.id ||
            ''
        )
      );
    }
  }, [
    bookings,
    selectedBookingId,
    activeBookingId,
  ]);

  const activeBooking = useMemo(() => {
    return (
      bookings.find(
        (booking) =>
          String(booking.id) ===
          String(activeBookingId)
      ) ||
      bookings[0] ||
      null
    );
  }, [bookings, activeBookingId]);

  const activeEntry = useMemo(() => {
    if (!activeBooking) return {};

    return (
      techLogMap[String(activeBooking.id)] || {}
    );
  }, [activeBooking, techLogMap]);

  const activeDraft = useMemo(() => {
    if (!activeBooking) return null;

    const bookingKey = String(activeBooking.id);

    return (
      draftMap[bookingKey] ||
      buildDraft(activeBooking, activeEntry)
    );
  }, [activeBooking, activeEntry, draftMap]);

  const activePilot = activeBooking
    ? userMap[String(activeBooking.user_id)] || null
    : null;

  function updateDraft(field, value) {
    if (!activeBooking) return;

    const bookingKey = String(activeBooking.id);

    setDraftMap((previous) => ({
      ...previous,
      [bookingKey]: {
        ...(previous[bookingKey] ||
          buildDraft(activeBooking, activeEntry)),
        [field]: value,
      },
    }));
  }

  function handleSaveDraft() {
    if (!activeBooking || !activeDraft) return;

    onSaveDraft?.(activeBooking, activeDraft);
  }

  function handleAcceptEntry() {
    if (!activeBooking || !activeDraft) return;

    onAcceptEntry?.(activeBooking, activeDraft);
  }

  return (
    <div style={styles.workspace}>
      <section style={styles.reviewSection}>
        <div style={styles.reviewHeader}>
          <div>
            <h2 style={styles.sectionTitle}>
              Outstanding Tech Logs
            </h2>

            <div style={styles.sectionMeta}>
              {bookings.length}{' '}
              {bookings.length === 1
                ? 'entry'
                : 'entries'}{' '}
              awaiting review
            </div>
          </div>

          <div style={styles.aircraftIdentity}>
            <div style={styles.registration}>
              {aircraft?.registration ||
                'Aircraft'}
            </div>

            {aircraft?.icao_type ? (
              <div style={styles.aircraftType}>
                {aircraft.icao_type}
              </div>
            ) : null}
          </div>
        </div>

        {bookings.length === 0 ? (
          <div style={styles.emptyState}>
            No outstanding tech log entries for this
            aircraft.
          </div>
        ) : (
          <div style={styles.console}>
            <aside style={styles.queuePane}>
              <div style={styles.paneHeader}>
                Queue
              </div>

              <div style={styles.queueList}>
                {bookings.map((booking) => {
                  const pilot =
                    userMap[
                      String(booking.user_id)
                    ] || null;

                  const isActive =
                    String(booking.id) ===
                    String(activeBooking?.id);

                  return (
                    <button
                      key={booking.id}
                      type="button"
                      onClick={() =>
                        setActiveBookingId(
                          String(booking.id)
                        )
                      }
                      style={{
                        ...styles.queueItem,
                        ...(isActive
                          ? styles.queueItemActive
                          : {}),
                      }}
                    >
                      <div
                        style={styles.queueTopRow}
                      >
                        <span
                          style={
                            styles.queueAircraft
                          }
                        >
                          {aircraft?.registration ||
                            `Aircraft ${booking.aircraft_id}`}
                        </span>

                        <span
                          style={
                            styles.queueStatus
                          }
                        >
                          Awaiting
                        </span>
                      </div>

                      <div
                        style={styles.queuePilot}
                      >
                        {pilot?.full_name ||
                          pilot?.email ||
                          'Unknown pilot'}
                      </div>

                      <div
                        style={styles.queueBottomRow}
                      >
                        <span>
                          {formatRoute(booking)}
                        </span>

                        <span>
                          {formatShortDate(
                            booking.date
                          )}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>

            {activeBooking && activeDraft ? (
              <>
                <main style={styles.submissionPane}>
                  <div style={styles.paneHeaderRow}>
                    <div>
                      <div style={styles.paneHeader}>
                        Pilot Submission
                      </div>

                      <div
                        style={styles.submissionMeta}
                      >
                        {activePilot?.full_name ||
                          activePilot?.email ||
                          'Unknown pilot'}
                        {' · '}
                        {formatDate(
                          activeBooking.date
                        )}
                      </div>
                    </div>

                    <div
                      style={styles.contactActions}
                    >
                      {activePilot?.phone ? (
                        <a
                          href={`tel:${activePilot.phone}`}
                          style={
                            styles.contactButton
                          }
                          title={`Call ${activePilot.phone}`}
                        >
                          Call
                        </a>
                      ) : null}

                      {activePilot?.email ? (
                        <a
                          href={`mailto:${activePilot.email}`}
                          style={
                            styles.contactButton
                          }
                          title={`Email ${activePilot.email}`}
                        >
                          Email
                        </a>
                      ) : null}
                    </div>
                  </div>

                  <div style={styles.recordGrid}>
                    <RecordField
                      label="From"
                      value={
                        activeDraft.departureAirfield
                      }
                    />

                    <RecordField
                      label="To"
                      value={
                        activeDraft.arrivalAirfield
                      }
                    />

                    <RecordField
                      label="Departure"
                      value={
                        activeDraft.departureTime
                      }
                    />

                    <RecordField
                      label="Arrival"
                      value={activeDraft.arrivalTime}
                    />

                    <RecordField
                      label="Flight time"
                      value={formatFlightTime(
                        activeDraft.flightTime
                      )}
                    />

                    <RecordField
                      label="Fuel uplift"
                      value={formatQuantity(
                        activeDraft.fuelUpliftLitres,
                        'L'
                      )}
                    />

                    <RecordField
                      label="Oil uplift"
                      value={formatQuantity(
                        activeDraft.oilUpliftLitres,
                        'L'
                      )}
                    />

                    <RecordField
                      label="Signature"
                      value={activeDraft.signature}
                    />
                  </div>

                  <div style={styles.recordBlock}>
                    <div style={styles.recordLabel}>
                      Aircraft condition
                    </div>

                    <div
                      style={{
                        ...styles.conditionBadge,
                        ...(activeDraft.aircraftStatus ===
                        'defect_reported'
                          ? styles.conditionBadgeDefect
                          : styles.conditionBadgeClear),
                      }}
                    >
                      {activeDraft.aircraftStatus ===
                      'defect_reported'
                        ? 'Defect reported'
                        : 'Serviceable'}
                    </div>
                  </div>

                  <div style={styles.recordBlock}>
                    <div style={styles.recordLabel}>
                      Defect details
                    </div>

                    <div
                      style={{
                        ...styles.recordText,
                        ...(hasDefect(
                          activeDraft.defect
                        )
                          ? styles.defectText
                          : {}),
                      }}
                    >
                      {activeDraft.defect ||
                        'Nil'}
                    </div>
                  </div>

                  <div style={styles.recordBlock}>
                    <div style={styles.recordLabel}>
                      Pilot remarks
                    </div>

                    <div style={styles.recordText}>
                      {activeDraft.remarks ||
                        'No remarks recorded.'}
                    </div>
                  </div>
                </main>

                <aside style={styles.reviewPane}>
                  <div style={styles.paneHeader}>
                    Owner Review
                  </div>

                  <div style={styles.reviewControl}>
                    <div style={styles.controlLabel}>
                      Review status
                    </div>

                    <div style={styles.statusOptions}>
                      <button
                        type="button"
                        onClick={() =>
                          updateDraft(
                            'reviewStatus',
                            'accepted'
                          )
                        }
                        style={{
                          ...styles.statusButton,
                          ...(activeDraft.reviewStatus ===
                          'accepted'
                            ? styles.statusButtonActive
                            : {}),
                        }}
                      >
                        Ready to accept
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          updateDraft(
                            'reviewStatus',
                            'clarification'
                          )
                        }
                        style={{
                          ...styles.statusButton,
                          ...(activeDraft.reviewStatus ===
                          'clarification'
                            ? styles.statusButtonWarning
                            : {}),
                        }}
                      >
                        Clarification needed
                      </button>
                    </div>
                  </div>

                  <label style={styles.notesField}>
                    <span style={styles.controlLabel}>
                      Review notes
                    </span>

                    <textarea
                      rows={7}
                      value={
                        activeDraft.reviewNotes || ''
                      }
                      onChange={(event) =>
                        updateDraft(
                          'reviewNotes',
                          event.target.value
                        )
                      }
                      style={styles.textarea}
                      placeholder="Record any clarification, amendment or acceptance note."
                    />
                  </label>

                  <div style={styles.reviewSummary}>
                    <SummaryRow
                      label="Booking"
                      value={`#${activeBooking.id}`}
                    />

                    <SummaryRow
                      label="Pilot"
                      value={
                        activePilot?.full_name ||
                        activePilot?.email ||
                        'Unknown'
                      }
                    />

                    <SummaryRow
                      label="Route"
                      value={formatRoute(
                        activeBooking
                      )}
                    />
                  </div>

                  <div style={styles.actionStack}>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      style={{
                        ...ds.buttons.secondary,
                        width: '100%',
                        justifyContent: 'center',
                      }}
                    >
                      Save draft
                    </button>

                    <button
                      type="button"
                      onClick={handleAcceptEntry}
                      disabled={
                        activeDraft.reviewStatus !==
                        'accepted'
                      }
                      style={{
                        ...ds.buttons.primary,
                        width: '100%',
                        justifyContent: 'center',
                        opacity:
                          activeDraft.reviewStatus ===
                          'accepted'
                            ? 1
                            : 0.48,
                        cursor:
                          activeDraft.reviewStatus ===
                          'accepted'
                            ? 'pointer'
                            : 'not-allowed',
                      }}
                    >
                      Accept into live tech log
                    </button>

                    {saveNoticeMap[
                      `${activeBooking.id}:tech`
                    ] ? (
                      <div style={styles.saveNotice}>
                        {
                          saveNoticeMap[
                            `${activeBooking.id}:tech`
                          ]
                        }
                      </div>
                    ) : null}
                  </div>
                </aside>
              </>
            ) : null}
          </div>
        )}
      </section>

      <section style={styles.logSection}>
        <div style={styles.logHeader}>
          <div>
            <h2 style={styles.sectionTitle}>
              Aircraft Technical Log
            </h2>

            <div style={styles.sectionMeta}>
              Accepted aircraft history
            </div>
          </div>

          <div style={styles.logControls}>
            <label style={styles.annualControl}>
              <span style={styles.controlLabel}>
                Next annual
              </span>

              <input
                type="date"
                value={annualCheckDate || ''}
                onChange={(event) =>
                  onAnnualCheckDateChange?.(
                    event.target.value
                  )
                }
                style={styles.dateInput}
              />
            </label>

            <div style={styles.annualStatus}>
              {formatAnnualStatus(
                annualCountdownDays
              )}
            </div>

            <button
              type="button"
              onClick={onExport}
              style={ds.buttons.secondary}
            >
              Export XLSX
            </button>
          </div>
        </div>

        <div style={styles.liveLogArea}>
          {liveLog}
        </div>
      </section>
    </div>
  );
}

function RecordField({ label, value }) {
  return (
    <div style={styles.recordField}>
      <div style={styles.recordLabel}>
        {label}
      </div>

      <div style={styles.recordValue}>
        {value || 'Not recorded'}
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={styles.summaryRow}>
      <span style={styles.summaryLabel}>
        {label}
      </span>

      <span style={styles.summaryValue}>
        {value}
      </span>
    </div>
  );
}

function buildDraft(booking, entry) {
  const briefing =
    booking?.briefing_data &&
    typeof booking.briefing_data === 'object'
      ? booking.briefing_data
      : {};

  const departureAirfield =
    entry?.departureAirfield ||
    briefing.departureAirfield ||
    booking?.departure_airfield ||
    '';

  const arrivalAirfield =
    entry?.arrivalAirfield ||
    briefing.destinationAirfield ||
    booking?.destination_airfield ||
    '';

  const defect =
    entry?.defect ||
    entry?.snag ||
    'Nil';

  return {
    flightNumber:
      entry?.flightNumber ||
      String(booking?.id || ''),

    departureAirfield: String(
      departureAirfield || ''
    ).toUpperCase(),

    arrivalAirfield: String(
      arrivalAirfield || ''
    ).toUpperCase(),

    departureTime:
      entry?.departureTime ||
      booking?.time ||
      '',

    arrivalTime:
      entry?.arrivalTime ||
      booking?.end_time ||
      '',

    flightTime: entry?.flightTime || '',

    fuelUpliftLitres:
      entry?.fuelUpliftLitres || '',

    oilUpliftLitres:
      entry?.oilUpliftLitres || '',

    aircraftStatus:
      entry?.aircraftStatus ||
      (String(defect).toLowerCase() === 'nil'
        ? 'serviceable'
        : 'defect_reported'),

    defect,

    signature: entry?.signature || '',

    remarks:
      entry?.remarks ||
      entry?.notes ||
      '',

    reviewStatus:
      entry?.reviewStatus || 'pending',

    reviewNotes:
      entry?.reviewNotes || '',
  };
}

function formatDate(value) {
  const text = String(value || '').trim();

  if (!text) return 'Date not recorded';

  const match = text.match(
    /^(\d{4})-(\d{2})-(\d{2})$/
  );

  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }

  return text;
}

function formatShortDate(value) {
  const text = String(value || '').trim();

  const match = text.match(
    /^(\d{4})-(\d{2})-(\d{2})$/
  );

  if (!match) {
    return text || 'No date';
  }

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
  });
}

function formatRoute(booking) {
  const briefing =
    booking?.briefing_data &&
    typeof booking.briefing_data === 'object'
      ? booking.briefing_data
      : {};

  const departure = String(
    briefing.departureAirfield ||
      booking?.departure_airfield ||
      ''
  )
    .trim()
    .toUpperCase();

  const arrival = String(
    briefing.destinationAirfield ||
      booking?.destination_airfield ||
      ''
  )
    .trim()
    .toUpperCase();

  const purpose = String(
    booking?.purpose ||
      briefing.flightPurpose ||
      briefing.purpose ||
      ''
  ).toLowerCase();

  if (purpose.includes('circuit')) {
    return departure
      ? `Circuits · ${departure}`
      : 'Circuit training';
  }

  if (
    departure &&
    arrival &&
    departure === arrival
  ) {
    return `Local · ${departure}`;
  }

  if (departure || arrival) {
    return `${departure || 'Not set'} → ${
      arrival || 'Not set'
    }`;
  }

  return 'Route not recorded';
}

function formatAnnualStatus(days) {
  if (!Number.isFinite(days)) {
    return 'Annual date not set';
  }

  if (days < 0) {
    return `${Math.abs(days)} days overdue`;
  }

  if (days === 0) {
    return 'Due today';
  }

  return `${days} days remaining`;
}

function formatFlightTime(value) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return 'Not recorded';
  }

  return String(value);
}

function formatQuantity(value, unit) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return 'Nil';
  }

  return `${value} ${unit}`;
}

function hasDefect(value) {
  const text = String(value || '')
    .trim()
    .toLowerCase();

  return Boolean(
    text &&
      text !== 'nil' &&
      text !== 'none' &&
      text !== 'no defect'
  );
}

const styles = {
  workspace: {
    display: 'grid',
    gridTemplateRows:
      'minmax(440px, 50vh) minmax(420px, 50vh)',
    gap: '0.75rem',
    width: '100%',
    color: '#e7ebf0',
  },

  reviewSection: {
    minHeight: 0,
    overflow: 'hidden',
    background: '#171a1f',
    border: '1px solid #30353d',
    borderRadius: 10,
    display: 'flex',
    flexDirection: 'column',
  },

  logSection: {
    minHeight: 0,
    overflow: 'hidden',
    background: '#171a1f',
    border: '1px solid #30353d',
    borderRadius: 10,
    display: 'flex',
    flexDirection: 'column',
  },

  reviewHeader: {
    minHeight: 58,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.7rem 0.9rem',
    borderBottom: '1px solid #30353d',
  },

  logHeader: {
    minHeight: 58,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.65rem 0.9rem',
    borderBottom: '1px solid #30353d',
    flexWrap: 'wrap',
  },

  sectionTitle: {
    margin: 0,
    color: '#f3f5f7',
    fontSize: '1rem',
    fontWeight: 650,
    letterSpacing: '-0.01em',
  },

  sectionMeta: {
    marginTop: 3,
    color: '#8f98a3',
    fontSize: '0.72rem',
  },

  aircraftIdentity: {
    textAlign: 'right',
  },

  registration: {
    color: '#f3f5f7',
    fontSize: '0.92rem',
    fontWeight: 700,
  },

  aircraftType: {
    marginTop: 2,
    color: '#8f98a3',
    fontSize: '0.68rem',
  },

  console: {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns:
      'minmax(190px, 0.75fr) minmax(330px, 1.7fr) minmax(230px, 0.9fr)',
  },

  queuePane: {
    minWidth: 0,
    overflow: 'hidden',
    borderRight: '1px solid #30353d',
    display: 'flex',
    flexDirection: 'column',
    background: '#15181d',
  },

  submissionPane: {
    minWidth: 0,
    overflow: 'auto',
    padding: '0.8rem',
    borderRight: '1px solid #30353d',
    background: '#1b1e23',
  },

  reviewPane: {
    minWidth: 0,
    overflow: 'auto',
    padding: '0.8rem',
    background: '#181b20',
  },

  paneHeader: {
    color: '#c9cfd6',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.045em',
    textTransform: 'uppercase',
  },

  paneHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '0.75rem',
    paddingBottom: '0.7rem',
    marginBottom: '0.7rem',
    borderBottom: '1px solid #30353d',
  },

  submissionMeta: {
    marginTop: 4,
    color: '#8f98a3',
    fontSize: '0.72rem',
  },

  queueList: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: '0.45rem',
    display: 'grid',
    alignContent: 'start',
    gap: '0.35rem',
  },

  queueItem: {
    width: '100%',
    minHeight: 64,
    padding: '0.55rem 0.6rem',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 7,
    color: '#dce1e6',
    textAlign: 'left',
    cursor: 'pointer',
  },

  queueItemActive: {
    background: '#222a32',
    border: '1px solid #50606e',
  },

  queueTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.4rem',
  },

  queueAircraft: {
    fontSize: '0.77rem',
    fontWeight: 700,
  },

  queueStatus: {
    color: '#d7ad62',
    fontSize: '0.61rem',
    fontWeight: 650,
  },

  queuePilot: {
    marginTop: 4,
    color: '#bec5cc',
    fontSize: '0.72rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  queueBottomRow: {
    marginTop: 4,
    display: 'flex',
    justifyContent: 'space-between',
    gap: '0.5rem',
    color: '#7f8994',
    fontSize: '0.65rem',
  },

  contactActions: {
    display: 'flex',
    gap: '0.35rem',
  },

  contactButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.32rem 0.48rem',
    border: '1px solid #46515c',
    borderRadius: 6,
    color: '#cbd2d9',
    fontSize: '0.68rem',
    fontWeight: 650,
    textDecoration: 'none',
    background: '#20252b',
  },

  recordGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(4, minmax(0, 1fr))',
    borderTop: '1px solid #343940',
    borderLeft: '1px solid #343940',
  },

  recordField: {
    minWidth: 0,
    padding: '0.55rem',
    borderRight: '1px solid #343940',
    borderBottom: '1px solid #343940',
  },

  recordLabel: {
    color: '#858f9a',
    fontSize: '0.64rem',
    fontWeight: 650,
    textTransform: 'uppercase',
    letterSpacing: '0.035em',
  },

  recordValue: {
    marginTop: 5,
    color: '#e3e7eb',
    fontSize: '0.78rem',
    fontWeight: 550,
    overflowWrap: 'anywhere',
  },

  recordBlock: {
    padding: '0.65rem 0',
    borderBottom: '1px solid #30353d',
  },

  recordText: {
    marginTop: 6,
    color: '#d9dee3',
    fontSize: '0.76rem',
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
  },

  defectText: {
    color: '#e2ba76',
  },

  conditionBadge: {
    display: 'inline-flex',
    marginTop: 6,
    padding: '0.25rem 0.45rem',
    borderRadius: 5,
    fontSize: '0.68rem',
    fontWeight: 650,
  },

  conditionBadgeClear: {
    color: '#9fc8ac',
    background: 'rgba(90, 140, 105, 0.14)',
    border: '1px solid rgba(110, 165, 125, 0.28)',
  },

  conditionBadgeDefect: {
    color: '#e3b66a',
    background: 'rgba(173, 116, 43, 0.14)',
    border: '1px solid rgba(193, 135, 58, 0.3)',
  },

  reviewControl: {
    marginTop: '0.75rem',
  },

  controlLabel: {
    color: '#8d97a1',
    fontSize: '0.66rem',
    fontWeight: 650,
  },

  statusOptions: {
    display: 'grid',
    gap: '0.4rem',
    marginTop: '0.4rem',
  },

  statusButton: {
    width: '100%',
    padding: '0.5rem 0.55rem',
    border: '1px solid #3c424a',
    borderRadius: 6,
    background: '#20242a',
    color: '#bfc6cd',
    fontSize: '0.72rem',
    fontWeight: 600,
    textAlign: 'left',
    cursor: 'pointer',
  },

  statusButtonActive: {
    color: '#b9d9c1',
    background: 'rgba(78, 125, 90, 0.18)',
    border: '1px solid rgba(116, 166, 128, 0.45)',
  },

  statusButtonWarning: {
    color: '#e3bd79',
    background: 'rgba(150, 103, 43, 0.18)',
    border: '1px solid rgba(191, 139, 69, 0.46)',
  },

  notesField: {
    display: 'grid',
    gap: '0.4rem',
    marginTop: '0.85rem',
  },

  textarea: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.55rem',
    border: '1px solid #3b424a',
    borderRadius: 6,
    background: '#121519',
    color: '#e5e8eb',
    fontFamily: 'inherit',
    fontSize: '0.74rem',
    lineHeight: 1.45,
    resize: 'vertical',
  },

  reviewSummary: {
    marginTop: '0.85rem',
    padding: '0.55rem',
    border: '1px solid #30353d',
    borderRadius: 6,
    background: '#15181c',
  },

  summaryRow: {
    display: 'grid',
    gridTemplateColumns: '60px minmax(0, 1fr)',
    gap: '0.5rem',
    padding: '0.22rem 0',
  },

  summaryLabel: {
    color: '#77818c',
    fontSize: '0.64rem',
  },

  summaryValue: {
    color: '#cbd1d7',
    fontSize: '0.68rem',
    textAlign: 'right',
    overflowWrap: 'anywhere',
  },

  actionStack: {
    display: 'grid',
    gap: '0.45rem',
    marginTop: '0.85rem',
  },

  saveNotice: {
    color: '#9fc8ac',
    fontSize: '0.68rem',
    textAlign: 'center',
  },

  logControls: {
    display: 'flex',
    alignItems: 'end',
    gap: '0.6rem',
    flexWrap: 'wrap',
  },

  annualControl: {
    display: 'grid',
    gap: 3,
  },

  dateInput: {
    height: 34,
    boxSizing: 'border-box',
    padding: '0.35rem 0.45rem',
    border: '1px solid #3b424a',
    borderRadius: 6,
    background: '#121519',
    color: '#e2e6ea',
    fontSize: '0.7rem',
  },

  annualStatus: {
    paddingBottom: '0.45rem',
    color: '#aeb5bc',
    fontSize: '0.7rem',
  },

  liveLogArea: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    background: '#ffffff',
    borderTop: '1px solid #30353d',
  },

  emptyState: {
    margin: '0.8rem',
    padding: '0.9rem',
    border: '1px solid #30353d',
    borderRadius: 7,
    color: '#929ba5',
    background: '#15181d',
    fontSize: '0.78rem',
  },
};